<!-- <template>
  <div class="container">
    <div class="chart-wrapper">
      <div id="productComparison" style="width: 100%;height:450px;"></div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { onMounted, ref, watch, onUnmounted, reactive } from 'vue';

export default {
  name: 'ProductComparison',
  props: {
    xAxisData: {
      type: Array,
      required: true,
      default: () => []
    },
    seriesData: {
      type: Array,
      required: true,
      default: () => []
    },
    verticalLabel: {
      type: String,
      required: true,
      default: ''
    }
  },
  setup(props, { emit }) {
    const myChart = ref(null);
    const selectedPoints = reactive({});
    const processedData = ref([]);
    const isSmallScreen = ref(false);

    // 处理数据：按X轴索引映射，确保点位置正确
    const processSeriesData = (rawData) => {
      return rawData.map((item, index) => {
        const xAxisIndex = props.xAxisData.indexOf(item[0]);
        if (xAxisIndex === -1) return null; // 过滤不在X轴中的数据

        const uniqueId = `${item[0]}-${item[1].id}-${index}`;
        return {
          name: item[0],
          value: [xAxisIndex, item[1].value], // X轴用索引，确保位置正确
          productId: item[1].id,
          uniqueId: uniqueId,
          selected: !!selectedPoints[uniqueId]
        };
      }).filter(Boolean);
    };

    // 更新选中点样式
    const updatePointStyles = () => {
      if (!myChart.value) return;

      myChart.value.setOption({
        series: [{
          itemStyle: {
            color: params => !!selectedPoints[params.data.uniqueId] ? '#ea4335' : '#4285f4'
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          }
        }]
      });
    };

    const initChart = () => {
      if (!myChart.value) {
        myChart.value = echarts.init(document.getElementById('productComparison'));
      }
      updateChart();
      myChart.value.on('click', handleClick);
    };

    // 核心优化：添加文字样式（转速标签、界面文字）
    const updateChart = () => {
      processedData.value = processSeriesData(props.seriesData);
      if (!myChart.value) return;

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: params => {
            if (!params.value || !Array.isArray(params.value)) {
              return '数据格式异常';
            }
            const categoryName = props.xAxisData[params.value[0]] || '未知类别';
            const status = !!selectedPoints[params.data.uniqueId] ? '已选中' : '未选中';
            return `${categoryName}<br/>数值: ${params.value[1]}<br/>状态: ${status}`;
          }
        },
        // 数据缩放：添加X轴滚动条，解决类别过多问题
        dataZoom: [
          {
            type: 'slider',
            xAxisIndex: 0,
            height: 15,
            bottom: 5,
            start: 0,
            end: Math.min(100, 6 / props.xAxisData.length * 100) // 初始显示6个类别
          },
          { type: 'inside', xAxisIndex: 0 } // 支持鼠标滚轮缩放
        ],
        xAxis: {
          type: 'category',
          data: props.xAxisData,
          axisLabel: {
            color: '#c0c4cc',
            rotate: isSmallScreen.value ? 60 : 30, // 标签旋转60度，避免重叠
            interval: 0, // 强制显示所有标签
            margin: 15, // 增加标签与轴线距离
            fontSize: isSmallScreen.value ? 10 : 12 // 小屏幕缩小字体
          },
          axisTick: {
            alignWithLabel: true // 刻度与标签对齐
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            color: '#c0c4cc',
            fontSize: isSmallScreen.value ? 10 : 12
          },
          splitLine: {
            lineStyle: { color: 'rgba(255,255,255,0.1)' } // 弱化网格线
          }
        },
        series: [
          {
            symbolSize: isSmallScreen.value ? 10 : 12,
            data: processedData.value,
            type: 'scatter',
            itemStyle: {
              color: '#4285f4',
              symbolOffset: function(params) {
                // 从当前点的value中取x（x轴索引）和y（数值）
                const x = params.data.value[0]; // 修正：取value[0]
                const y = params.data.value[1]; // 修正：取value[1]

                // 筛选相同坐标的点（基于value中的x和y）
                const samePoints = processedData.value.filter(
                    item => item.value[0] === x && item.value[1] === y // 修正：用value筛选
                );

                if (samePoints.length <= 1) {
                  return [0, 0];
                }

                // 用uniqueId查找当前点在相同点中的索引（修正引用比较问题）
                const index = samePoints.findIndex(item => item.uniqueId === params.data.uniqueId);

                const total = samePoints.length;
                const offsetDistance = isSmallScreen.value ? 5 : 6; // 偏移距离（可根据需求调整）
                let offsetX;

                if (total % 2 === 1) {
                  const middle = Math.floor(total / 2);
                  offsetX = (index - middle) * offsetDistance; // 奇数个点：中间点为基准
                } else {
                  offsetX = (index - total / 2 + 0.5) * offsetDistance; // 偶数个点：中间间隙为基准
                }

                return [offsetX, 0]; // 仅水平偏移，垂直对齐
              }
            },
            emphasis: {
              scale: true,
              symbolSize: isSmallScreen.value ? 16 : 20
            }
          }
        ],
        // 新增：自定义文字样式（转速标签、界面文字）
        graphic: [
          // 左侧“转速(rpm)”标签
          {
            type: 'text',
            left: '2%',
            top: '50%',
            // 核心：通过transform旋转实现垂直效果
            rotation: Math.PI / 2, // 旋转90度（垂直）
            style: {
              text: props.verticalLabel,
              fill: '#c0c4cc',
              fontSize: 12,
              textAlign: 'center',
              textVerticalAlign: 'middle'
            },
            // 旋转后重新居中定位
            transformOrigin: '50% 50%'
          }
        ]
      };

      myChart.value.setOption(option);
      updatePointStyles();
    };

    // 点击事件处理
    const handleClick = (params) => {
      if (params.componentType === 'series' && params.seriesType === 'scatter') {
        const uniqueId = params.data.uniqueId;
        selectedPoints[uniqueId] = !selectedPoints[uniqueId]; // 切换状态

        updatePointStyles();

        const currentPoint = processedData.value.find(item => item.uniqueId === uniqueId);
        if (currentPoint) currentPoint.categoryName = props.xAxisData[currentPoint.value[0]];

        emit('point-selection-change', {
          point: currentPoint,
          isSelected: !!selectedPoints[uniqueId],
          allSelected: Object.keys(selectedPoints).map(id => {
            const point = processedData.value.find(item => item.uniqueId === id);
            if (point) point.categoryName = props.xAxisData[point.value[0]];
            return point;
          }).filter(Boolean)
        });
      }
    };

    // 屏幕尺寸检测
    const checkScreenSize = () => {
      isSmallScreen.value = window.innerWidth < 768; // 平板以下视为小屏幕
      if (myChart.value) updateChart();
    };

    const handleResize = () => {
      if (myChart.value) myChart.value.resize();
      checkScreenSize();
    };

    // 监听数据变化
    watch(
        () => [props.xAxisData, props.seriesData],
        () => updateChart(),
        { deep: true, immediate: true }
    );

    onMounted(() => {
      checkScreenSize();
      initChart();
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      if (myChart.value) {
        myChart.value.off('click', handleClick);
        myChart.value.dispose();
      }
    });

    return { selectedPoints };
  }
}
</script>

<style scoped>
.container {
  width: 100%;
  padding: 10px;
  box-sizing: border-box; /* 避免padding撑大容器 */
}
.chart-wrapper {
  width: 100%;
  min-width: 320px; /* 最小宽度，避免过度压缩 */
  overflow: hidden; /* 防止滚动条溢出 */
}
</style> -->






<template>
  <div class="container">
    <div class="chart-wrapper">
      <div id="productComparison" style="width: 100%;height:450px;"></div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { onMounted, ref, watch, onUnmounted, reactive, nextTick, getCurrentInstance } from 'vue';
import i18n from '@/lang';

export default {
  name: 'ProductComparison',
  props: {
    xAxisData: {
      type: Array,
      required: true,
      default: () => []
    },
    seriesData: {
      type: Array,
      required: true,
      default: () => []
    },
    verticalLabel: {
      type: String,
      required: true,
      default: ''
    }
  },
  setup(props, { emit }) {
    const $t = (key, params) => i18n.t(key, params);
    const myChart = ref(null);
    const selectedPoints = reactive({});
    const processedData = ref([]);
    const isSmallScreen = ref(false);

    // 处理数据：为相同位置的点计算偏移
    const processSeriesData = (rawData) => {
      const result = [];
      const positionMap = {};
      
      rawData.forEach((item, index) => {
        const xAxisIndex = props.xAxisData.indexOf(item[0]);
        if (xAxisIndex === -1) return;

        const uniqueId = `${item[0]}-${item[1].id}-${index}`;
        const positionKey = `${xAxisIndex}-${item[1].value}`;
        
        if (!positionMap[positionKey]) {
          positionMap[positionKey] = [];
        }
        positionMap[positionKey].push({
          name: item[0],
          value: [xAxisIndex, item[1].value],
          productId: item[1].id,
          uniqueId: uniqueId,
          selected: !!selectedPoints[uniqueId],
          positionKey: positionKey
        });
      });


      Object.keys(positionMap).forEach(positionKey => {
        const points = positionMap[positionKey];
        const totalPoints = points.length;
        
        if (totalPoints === 1) {
          result.push({
            ...points[0],
            symbolOffset: [0, 0]
          });
        } else {

          const pointSize = isSmallScreen.value ? 10 : 12; 
          const gap = pointSize * 1; 
          const spreadRange = (totalPoints - 1) * gap; 
          
          points.forEach((point, index) => {
            const offsetX = (index - (totalPoints - 1) / 2) * gap;
            result.push({
              ...point,
              symbolOffset: [offsetX, 0]
            });
          });
        }
      });

      return result;
    };

    // 更新选中点样式
    const updatePointStyles = () => {
      if (!myChart.value) return;

      myChart.value.setOption({
        series: [{
          data: processedData.value.map(item => ({
            ...item,
            itemStyle: {
              color: selectedPoints[item.uniqueId] ? '#ea4335' : '#4285f4'
            }
          }))
        }]
      });
    };

    const initChart = () => {
      if (!myChart.value) {
        myChart.value = echarts.init(document.getElementById('productComparison'));
      }
      updateChart();
      myChart.value.on('click', handleClick);
    };

    const updateChart = () => {
      processedData.value = processSeriesData(props.seriesData);
      if (!myChart.value) return;

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: params => {
            if (!params.data) return $t('chart.dataError');
            const categoryName = props.xAxisData[params.data.value[0]] || $t('chart.unknownCategory');
            const status = !!selectedPoints[params.data.uniqueId] ? $t('chart.selected') : $t('chart.unselected');
            return `${categoryName}<br/>${$t('chart.value')}: ${params.data.value[1]}<br/>${$t('chart.status')}: ${status}`;
          }
        },
        dataZoom: [
          {
            type: 'slider',
            xAxisIndex: 0,
            height: 15,
            bottom: 5,
            start: 0,
            end: Math.min(100, 6 / props.xAxisData.length * 100)
          },
          { type: 'inside', xAxisIndex: 0 }
        ],
        xAxis: {
          type: 'category',
          data: props.xAxisData,
          axisLabel: {
            color: '#c0c4cc',
            rotate: isSmallScreen.value ? 60 : 30,
            interval: 0,
            margin: 15,
            fontSize: isSmallScreen.value ? 10 : 12
          },
          axisTick: {
            alignWithLabel: true
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            color: '#c0c4cc',
            fontSize: isSmallScreen.value ? 10 : 12
          },
          splitLine: {
            lineStyle: { color: 'rgba(255,255,255,0.1)' }
          }
        },
        series: [
          {
            symbolSize: isSmallScreen.value ? 10 : 12,
            data: processedData.value,
            type: 'scatter',
            symbol: 'circle',
            itemStyle: {
              color: params => !!selectedPoints[params.data.uniqueId] ? '#ea4335' : '#4285f4'
            },
            emphasis: {
              scale: true,
              symbolSize: isSmallScreen.value ? 16 : 20,
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.3)'
              }
            }
          }
        ],
        graphic: [
          {
            type: 'text',
            left: '2%',
            top: '50%',
            rotation: Math.PI / 2,
            style: {
              text: props.verticalLabel,
              fill: '#c0c4cc',
              fontSize: 12,
              textAlign: 'center',
              textVerticalAlign: 'middle'
            },
            transformOrigin: '50% 50%'
          }
        ]
      };

      myChart.value.setOption(option, true);
    };

    // 点击事件处理
    const handleClick = (params) => {
      if (params.componentType === 'series' && params.seriesType === 'scatter') {
        const uniqueId = params.data.uniqueId;
        selectedPoints[uniqueId] = !selectedPoints[uniqueId];

        // 更新数据中的选中状态
        const pointIndex = processedData.value.findIndex(item => item.uniqueId === uniqueId);
        if (pointIndex !== -1) {
          processedData.value[pointIndex].selected = !!selectedPoints[uniqueId];
        }

        updatePointStyles();

        const currentPoint = processedData.value.find(item => item.uniqueId === uniqueId);
        if (currentPoint) {
          currentPoint.categoryName = props.xAxisData[currentPoint.value[0]];
        }

        emit('point-selection-change', {
          point: currentPoint,
          isSelected: !!selectedPoints[uniqueId],
          allSelected: Object.keys(selectedPoints).map(id => {
            const point = processedData.value.find(item => item.uniqueId === id);
            if (point) point.categoryName = props.xAxisData[point.value[0]];
            return point;
          }).filter(Boolean)
        });
      }
    };

    // 屏幕尺寸检测
    const checkScreenSize = () => {
      isSmallScreen.value = window.innerWidth < 768;
      if (myChart.value) {
        nextTick(() => {
          updateChart();
        });
      }
    };

    const handleResize = () => {
      if (myChart.value) myChart.value.resize();
      checkScreenSize();
    };

    // 监听数据变化
    watch(
        () => [props.xAxisData, props.seriesData],
        () => {
          if (myChart.value) {
            nextTick(() => {
              updateChart();
            });
          }
        },
        { deep: true, immediate: true }
    );

    onMounted(() => {
      checkScreenSize();
      initChart();
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      if (myChart.value) {
        myChart.value.off('click', handleClick);
        myChart.value.dispose();
      }
    });

    return { selectedPoints };
  }
}
</script>

<style scoped>
.container {
  width: 100%;
  padding: 10px;
  box-sizing: border-box;
}
.chart-wrapper {
  width: 100%;
  min-width: 320px;
  overflow: hidden;
}
</style>